import React from 'react';
import { FiSettings } from 'react-icons/fi';
import {
  FaCalendar,
  FaEuroSign,
  FaFilterCircleDollar,
  FaHardDrive,
  FaListCheck,
  FaMoneyBillTransfer,
  FaStar,
  FaTicket,
} from 'react-icons/fa6';
import { BiSolidPhotoAlbum } from 'react-icons/bi';
import { FaBalanceScale, FaMoneyBill, FaChartLine } from 'react-icons/fa';
import { HiOutlineChatBubbleLeftEllipsis, HiOutlineHome, HiUserGroup } from 'react-icons/hi2';
import { useSelector } from 'react-redux';
import { TbScoreboard } from 'react-icons/tb';
import useBusinessTranslation from '../../../../../hooks/useBusinessTranslation';
import DropdownItem from './DropdownItem';
import Navigation from './Navigation';
import { isCommunity, isMoments, ALLOWED_FINANCIAL_BUSINESS_IDS } from '../../../../../constants';
import { RootState } from '../../../../../redux/reducers';
import { SubscriptionType } from '../../../../../types/misc';
import useActiveSubscription from '../../../../../hooks/business/useActiveSubscription';
import useSelectedBusiness from '../../../../../hooks/business/useSelectedBusiness';

export default function DropdownPanel(): JSX.Element {
  const { t } = useBusinessTranslation();
  const {
    application: { onboardingStep },
    connections,
  } = useSelector((state: RootState) => state);
  const business = useSelectedBusiness();
  const role = useSelector((state: RootState) => state.application.role);
  const activeSubscription = useActiveSubscription();
  const hasSubscription = activeSubscription !== SubscriptionType.FREE;
  const connectionsWithAddress = connections.filter((c) => c.fields.ADDRESS);

  const financial = [
    {
      icon: <FaChartLine className="h-5 w-5 flex-shrink-0" />,
      name: t('page.financial.overview.title'),
      condition: isCommunity,
      path: '/financial/overview',
    },
    {
      icon: <FaMoneyBillTransfer className="h-5 w-5 flex-shrink-0" />,
      name: t('page.financial.administration.title'),
      condition: isCommunity && business?.features?.FINANCIAL_ADMINISTRATION,
      path: '/financial/administration',
    },
    {
      icon: <FaFilterCircleDollar className="h-5 w-5 flex-shrink-0" />,
      name: t('page.financial.salesCollect.title'),
      condition: isCommunity,
      path: '/financial/sales-collect',
    },
    {
      icon: <FaBalanceScale className="h-5 w-5 flex-shrink-0" />,
      name: t('page.financial.reports.title'),
      condition: isCommunity,
      path: '/financial/reports',
    },
  ];

  const features = [
    {
      icon: <FaTicket className="h-5 w-5 flex-shrink-0" />,
      name: t('page.dashboard.quickAction.orderAddressLabels'),
      condition: isMoments,
      path: '/payment/address-label',
      state: { connections: connectionsWithAddress },
    },
    {
      icon: <FaCalendar className="h-5 w-5 flex-shrink-0" />,
      name: t('page.event.regular.title'),
      condition: isCommunity && business?.features?.EVENTS,
      path: '/events',
    },
    {
      icon: <TbScoreboard className="h-5 w-5 flex-shrink-0" />,
      name: t('page.lists.score.title'),
      condition: (isCommunity && business?.features?.STATS_AND_SCORES) || false,
      path: '/lists/score',
    },
    {
      icon: <FaListCheck className="h-5 w-5 flex-shrink-0" />,
      name: t('page.lists.task.title'),
      condition: (isCommunity && business?.features?.TASKS) || false,
      path: '/lists/task',
    },
    {
      icon: <BiSolidPhotoAlbum className="h-5 w-5 flex-shrink-0" />,
      name: t('page.albums.title'),
      condition: (isCommunity && business?.features?.PHOTO_ALBUMS) || false,
      path: '/albums',
    },
    {
      icon: <FaHardDrive className="h-5 w-5 flex-shrink-0" />,
      name: t('page.files.title'),
      condition: (isCommunity && business?.features?.FILES) || false,
      path: '/files',
    }
  ];

  const management = [
    {
      icon: <FaEuroSign className="h-5 w-5 flex-shrink-0" />,
      name: t('page.settings.subscriptions.title'),
      condition: isCommunity && !hasSubscription,
      path: '/settings/subscriptions',
    },
    {
      icon: <FaStar className="h-5 w-5 flex-shrink-0" />,
      name: t('page.settings.subscriptions.manageSubscriptionLink'),
      condition: isCommunity && hasSubscription,
      path: '/subscriptions/manage',
    },
    {
      icon: <FaMoneyBill className="h-5 w-5 flex-shrink-0" />,
      name: t('page.affiliate.title'),
      condition: isCommunity,
      path: '/affiliate',
    },
    {
      icon: <FiSettings className="h-5 w-5 flex-shrink-0" />,
      name: t('page.settings.general.title'),
      id: 'settings',
      path: '/settings',
    },
  ];

  return (
    <>
      <Navigation
        id="overview"
        icon={<HiOutlineHome className="h-5 w-5" />}
        name={t('general.overview')}
        path="/overview"
      />
      <Navigation
        id="contacts"
        icon={<HiUserGroup className="h-5 w-5" />}
        name={t('page.dashboard.clientList.title')}
        path="/contacts"
      />
      {isCommunity && (
        <Navigation
          id="communication"
          icon={<HiOutlineChatBubbleLeftEllipsis className="h-5 w-5" />}
          name={t('general.communication')}
          path="/communication"
        />
      )}
      {features
        .filter((r) => r.condition === undefined || r.condition)
        .map((r) => (
          <Navigation {...r} key={r.path} />
        ))}
      {/* Until the feature is released, only show this to admin users or allowed business IDs when not in development */}
      {isCommunity &&
        (role === 'ADMIN' ||
          process.env.NODE_ENV === 'development' ||
          (process.env.NODE_ENV !== 'development' &&
            business?.businessId !== undefined &&
            ALLOWED_FINANCIAL_BUSINESS_IDS.includes(business.businessId))) && (
          <DropdownItem
            id="financial"
            duration={onboardingStep !== undefined ? 100 : undefined}
            name={t('page.financial.title')}
            icon={FaEuroSign}>
            {financial.map((r) => (
              <Navigation {...r} key={r.path} open />
            ))}
          </DropdownItem>
        )}
      {management
        .filter((r) => r.condition === undefined || r.condition)
        .map((r) => (
          <Navigation {...r} key={r.path} />
        ))}
    </>
  );
}
